import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { useListingPaths } from '../lib/listingPaths';
import { useMutation } from '../lib/useMutation';
import { money, shortDate } from '../lib/format';
import { Alert, Button, Card, ErrorBlock, Field, LoadingBlock, TextInput } from '../components/ui';

/**
 * Promoting a live rental listing on Find Your Next Home.
 *
 * Honest about the thing landlords find surprising, exactly as the sale pay
 * screen is: pressing the button does not promote anything. The money goes to
 * a Till, a person matches the code, and the listing moves up then. So the
 * button says "Tell us I have paid", and the waiting state says somebody is
 * checking.
 */

interface Option {
  code: string; name: string; description: string | null; tier: number; homepage: boolean;
  max_active: number | null; days: number; price: string; currency_code: string;
  once_per_account: boolean; free: boolean; available: boolean; spent: boolean;
  full: boolean; nextFreeAt: string | null;
}

interface Info {
  listing: { id: string; title: string; status: string; isSoldOut: boolean; rent: string };
  promotable: boolean;
  notPromotableBecause: string | null;
  active: { tier: number; homepage: boolean; promoted_until: string } | null;
  rates: Option[];
  ratesConfigured: boolean;
  payInfo: { tillNumber: string; tillName: string | null; instructions: string | null } | null;
  pending: {
    id: string; rate_name: string | null; tier: number; amount: string; days: number;
    payment_reference: string | null; requested_at: string;
  } | null;
  history: Array<{
    source: string; state: string; rate_name: string | null; tier: number; homepage: boolean;
    amount: string; days: number; covers_from: string | null; covers_until: string | null;
    grant_reason: string | null; rejected_reason: string | null; refund_reason: string | null;
    requested_at: string;
  }>;
}

const TIER: Record<number, string> = { 1: 'Featured', 2: 'Featured Plus', 3: 'Premium' };

/** What a placement does, when the operator has not written it. */
function defaultClaim(o: Option): string {
  const parts = [`${TIER[o.tier]} badge`, 'higher in search results'];
  if (o.tier >= 2) parts[1] = o.tier === 3 ? 'top of search results' : 'above Featured listings in search';
  if (o.homepage) parts.push('shown on the homepage');
  return parts.join(' · ');
}

export function ListingPromote() {
  const paths = useListingPaths();
  const { id } = useParams<{ id: string }>();
  const view = useApi<Info>(`/api/v1/listings/${id}/promotion`);
  const act = useMutation<{ free?: boolean; promotedUntil?: string }>();
  const [chosen, setChosen] = useState('');
  const [reference, setReference] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  if (view.loading) return <div className="page"><Card><LoadingBlock /></Card></div>;
  if (view.error) return <div className="page"><Card><ErrorBlock error={view.error} onRetry={view.reload} /></Card></div>;

  const d = view.data!;
  const selected = d.rates.find((r) => r.code === chosen);

  const submit = async (rateCode: string) => {
    setNotice(null);
    const res = await act.run(`/api/v1/listings/${id}/promotion`, {
      body: { rateCode, ...(reference.trim() ? { paymentReference: reference.trim() } : {}) },
    });
    setReference(''); setChosen('');
    setNotice(res.free
      ? `Done. Your listing is promoted until ${res.promotedUntil ? shortDate(res.promotedUntil) : 'the end of its window'}.`
      : 'Thank you. We will match your M-Pesa code to the payment and promote the listing, usually within a working day.');
    view.reload();
  };

  const withdraw = async () => {
    setNotice(null);
    await act.run(`/api/v1/listings/${id}/promotion`, { method: 'DELETE' });
    setNotice('Withdrawn. You can quote a different M-Pesa code now.');
    view.reload();
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Promote “{d.listing.title}”</h1>
          <p className="page-sub">
            Browsing is free for people looking for a home. Promotion puts your listing in front
            of more of them.
          </p>
        </div>
        <Link to={paths.listing(id!)}>Back to the listing</Link>
      </div>

      {notice && <Alert tone="success">{notice}</Alert>}
      {act.error && <Alert tone="danger">{act.error.message}</Alert>}

      <Card title="Right now">
        {d.active
          ? (
            <p>
              <span className="lst-featured">{TIER[d.active.tier]}</span>{' '}
              {d.active.homepage && 'On the homepage and '}
              promoted in search until <strong>{shortDate(d.active.promoted_until)}</strong>.
              Buying the same option again adds its days after this date, so nothing you have
              paid for is lost.
            </p>
          )
          : <p className="muted">This listing is not promoted. It appears in search by date listed.</p>}
      </Card>

      {!d.promotable ? (
        <Card title="Promotion is not available">
          <Alert tone="warning">{d.notPromotableBecause}</Alert>
        </Card>
      ) : d.pending ? (
        <Card title="We are checking your payment">
          <p>
            You told us you paid <strong>{money(d.pending.amount)}</strong> for{' '}
            {d.pending.rate_name ?? TIER[d.pending.tier]} ({d.pending.days} days)
            {d.pending.payment_reference && <> quoting <strong>{d.pending.payment_reference}</strong></>}
            {' '}on {shortDate(d.pending.requested_at)}.
          </p>
          <p className="muted">
            Nothing changes in search until somebody here has matched that code to the money.
            If you quoted the wrong code, withdraw this and send the right one.
          </p>
          <Button variant="secondary" onClick={withdraw} disabled={act.busy}>
            Withdraw and start again
          </Button>
        </Card>
      ) : !d.ratesConfigured ? (
        <Card title="Promotion is not on offer yet">
          <p className="muted">Your listing is live and visible in search. Check back soon.</p>
        </Card>
      ) : (
        <>
          <Card title="Choose a promotion">
            <div className="plan-grid">
              {d.rates.map((r) => (
                <label key={r.code} className={`plan-card${chosen === r.code ? ' current' : ''}`}>
                  <span className="plan-name">
                    <input
                      type="radio" name="rate" value={r.code}
                      checked={chosen === r.code} disabled={!r.available}
                      onChange={() => setChosen(r.code)}
                    />{' '}
                    {r.name}
                  </span>
                  <span className="plan-price">{r.free ? 'Free' : money(r.price)}</span>
                  <span className="muted">{r.days} days</span>
                  <span className="muted">{r.description || defaultClaim(r)}</span>
                  {r.spent && <span className="muted">Already used on this account</span>}
                  {r.full && (
                    <span className="muted">
                      All places taken{r.nextFreeAt ? ` until ${shortDate(r.nextFreeAt)}` : ''}
                    </span>
                  )}
                </label>
              ))}
            </div>
          </Card>

          {selected?.free ? (
            <Card title={selected.name}>
              <p>No payment needed.</p>
              <Button onClick={() => submit(selected.code)} disabled={act.busy}>
                {act.busy ? 'Working…' : 'Promote my listing'}
              </Button>
            </Card>
          ) : d.payInfo ? (
            <Card title="How to pay">
              <p>
                Pay <strong>{selected ? money(selected.price) : 'the amount of the option you chose'}</strong> to
                M-Pesa Till <strong>{d.payInfo.tillNumber}</strong>
                {d.payInfo.tillName && <> ({d.payInfo.tillName})</>}, then enter the confirmation
                code from the M-Pesa message.
              </p>
              {d.payInfo.instructions && <p className="muted">{d.payInfo.instructions}</p>}
              <Field label="M-Pesa confirmation code" htmlFor="ref">
                <TextInput
                  id="ref" value={reference} placeholder="e.g. SFH4K2LMN9" autoComplete="off"
                  onChange={(e) => setReference(e.target.value.toUpperCase())}
                />
              </Field>
              <Button
                onClick={() => selected && submit(selected.code)}
                disabled={act.busy || !selected || reference.trim().length < 4}
              >
                {act.busy ? 'Sending…' : 'Tell us I have paid'}
              </Button>
              <p className="muted" style={{ marginTop: 8 }}>
                Promotion starts once we have confirmed the payment, not before. This payment is
                separate from your subscription and from rent.
              </p>
            </Card>
          ) : (
            <Card title="How to pay">
              <Alert tone="warning">
                Online payment is not set up yet. Please contact us and we will promote this
                listing for you.
              </Alert>
            </Card>
          )}
        </>
      )}

      {d.history.length > 0 && (
        <Card title="Promotion history" bodyClassName="card-pad-0">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>When</th><th>Option</th><th className="num">Amount</th><th>Ran</th><th>Outcome</th></tr>
              </thead>
              <tbody>
                {d.history.map((h, i) => (
                  <tr key={i}>
                    <td>{shortDate(h.requested_at)}</td>
                    <td>{h.rate_name ?? TIER[h.tier]}</td>
                    <td className="num">{h.source === 'payment' ? money(h.amount) : 'Free'}</td>
                    <td>
                      {h.covers_from && h.covers_until
                        ? `${shortDate(h.covers_from)} – ${shortDate(h.covers_until)}`
                        : '—'}
                    </td>
                    <td>
                      {h.state === 'confirmed' ? 'Confirmed'
                        : h.state === 'rejected' ? `Not accepted — ${h.rejected_reason ?? ''}`
                          : h.state === 'refunded' ? `Refunded — ${h.refund_reason ?? ''}`
                            : h.state}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
